import { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../contexts/AppContext';
import { AGENT_ESCALATION, DEPT_DB_MAP, titleCase, BASE, authFetch } from '../../constants';
import { ActionItem, Stats } from './types';
import KpiGrid from '../../components/ui/KpiGrid';
import ActionButton from '../../components/ui/ActionButton';
import InboxTable from './InboxTable';
import ActionDetail from './ActionDetail';
import TransferModal from './TransferModal';
import EscalateModal from './EscalateModal';

const API = `${BASE}/api/v1/actions`;
const CONTROLS_API = `${BASE}/api/v1/controls`;
const PAGE_SIZE = 50;

export default function MyInbox() {
  const { role } = useApp();
  const [items, setItems] = useState<ActionItem[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [selected, setSelected] = useState<ActionItem | null>(null);
  const [notes, setNotes] = useState('');
  const [acting, setActing] = useState(false);
  const [filter, setFilter] = useState<'all' | 'PENDING' | 'IN_PROGRESS'>('all');
  const [loading, setLoading] = useState(true);
  // Toast
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  // Department switcher + pagination
  const [selectedDept, setSelectedDept] = useState('');
  const [deptCounts, setDeptCounts] = useState<Record<string, number>>({});
  const [deptTotal, setDeptTotal] = useState(0);
  const [inboxPage, setInboxPage] = useState(0);
  const [inboxTotal, setInboxTotal] = useState(0);
  // Transfer modal
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferDept, setTransferDept] = useState('');
  const [transferRole, setTransferRole] = useState('');
  const [transferLevel, setTransferLevel] = useState('L2');
  const [transferReason, setTransferReason] = useState('');
  const [transferFinding, setTransferFinding] = useState('');
  // Escalate modal
  const [showEscalate, setShowEscalate] = useState(false);
  const [escalateReason, setEscalateReason] = useState('');
  const [escalateFinding, setEscalateFinding] = useState('');
  // Flag issue (cascade review)
  const [flagReview, setFlagReview] = useState(false);
  // Control gap modal
  const [showControlGap, setShowControlGap] = useState(false);
  const [controlGap, setControlGap] = useState<any>(null);
  const [controlGapLoading, setControlGapLoading] = useState(false);

  const fetchControlGap = async (exceptionType: string) => {
    setControlGapLoading(true);
    setShowControlGap(true);
    setControlGap(null);
    try {
      const res = await authFetch(`${CONTROLS_API}/failure-pattern/${exceptionType}`);
      if (res.ok) {
        setControlGap(await res.json());
      } else {
        setControlGap({ error: true });
      }
    } catch (e) {
      console.error('Failed to fetch control gap', e);
      setControlGap({ error: true });
    }
    setControlGapLoading(false);
  };

  const getAgentForRole = useCallback((): string | null => {
    if (!role) return null;
    if (AGENT_ESCALATION[role.id]) return role.id;
    const roleToAgent: Record<string, string> = {
      cfo: 'chief_financial_officer',
      vp_finance: 'vp_finance',
      controller: 'controller_support',
      treasury_mgr: 'treasury_analyst',
      ap_supervisor: 'ap_supervisor',
      analyst: 'ap_accountant',
    };
    return roleToAgent[role.id] || null;
  }, [role]);

  const loadDeptCounts = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/department-counts`);
      if (res.ok) {
        const data = await res.json();
        setDeptCounts(data.departments || {});
        setDeptTotal(data.total || 0);
      }
    } catch (e) { console.error(e); showToast('Failed to load department counts', false); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const roleDept = Array.isArray(role.departments) ? role.departments[0] || 'all' : 'all';
    const params = new URLSearchParams();
    params.set('agent_level', role.level);
    params.set('agent_department', roleDept);

    try {
      const pendingRes = await authFetch(`${BASE}/api/v1/exceptions/pending-approval?${params}`);
      const data = await pendingRes.json();
      const actionable = data.actionable || [];

      const allItems: ActionItem[] = actionable.map((p: any) => ({
        id: p.id,
        exception_id: p.id,
        action_type: 'REVIEW',
        action_label: p.ai_recommendation || 'Review Required',
        action_source: 'SYSTEM',
        current_department: p.department || '',
        current_role: '',
        current_level: p.escalation_level || 'L2',
        status: 'PENDING',
        status_reason: null,
        resolution_option_id: null,
        resolution_confidence: p.ai_confidence || null,
        proposed_je: null, final_je: null,
        je_id: null, je_status: null,
        transfer_context: null,
        prior_findings: null,
        sla_hours: 24,
        due_at: p.sla_deadline || null,
        started_at: null,
        closed_at: null,
        created_at: p.created_at || null,
        is_overdue: p.sla_deadline ? new Date(p.sla_deadline) < new Date() : false,
        exception: {
          id: p.id,
          ref: p.exception_ref || `EXC-${p.id}`,
          type: p.exception_type || '',
          title: p.title || titleCase(p.exception_type) || '',
          amount: p.amount || 0,
          currency: p.currency || 'USD',
          entity: p.jurisdiction || '',
          severity: p.severity || 'MEDIUM',
          department: p.department || '',
          vendor_name: p.vendor_name || '',
        },
        ai_resolution: p.ai_recommendation ? {
          confidence: p.ai_confidence,
          recommendation: p.ai_recommendation,
          detail: p.recommendation_detail,
          action: p.ai_recommendation,
        } : undefined,
      }));

      setItems(allItems);
      setInboxTotal(allItems.length);

      const overdue = allItems.filter((i: ActionItem) => i.is_overdue).length;
      const withDue = allItems.filter((i: ActionItem) => i.due_at);
      const sla_compliance = withDue.length > 0
        ? Math.round(((withDue.length - overdue) / withDue.length) * 100)
        : 100;
      setStats({
        pending: allItems.length,
        in_review: 0,
        completed: 0,
        overdue,
        sla_compliance,
        total: allItems.length,
      });
    } catch (e) {
      console.error('Failed to load inbox', e);
      showToast('Failed to load inbox. Please try again.', false);
    }
    setLoading(false);
  }, [role.level, role.departments, selectedDept]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDeptCounts(); }, [loadDeptCounts]);

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  const doAction = async (actionId: number, action: string, body: any = {}) => {
    setActing(true);
    try {
      const res = await authFetch(`${API}/${actionId}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        const jeId = data.je_id || data.next_action?.je_id;
        const msg = jeId
          ? `${data.message || `Action ${action} completed`} — JE #${jeId} posted. Download package from detail panel.`
          : data.message || `Action ${action} completed`;
        showToast(msg, true);
        setSelected(null);
        setNotes('');
        setFlagReview(false);
        load();
        loadDeptCounts();
      } else {
        const err = await res.json();
        showToast(`Error: ${err.detail || 'Unknown error'}`, false);
      }
    } catch (e) {
      console.error('Action failed', e);
      showToast('Action failed — check connection', false);
    }
    setActing(false);
  };

  const handleTransfer = async () => {
    if (!selected || !transferReason.trim() || !transferDept) return;
    setActing(true);
    try {
      const res = await authFetch(`${API}/${selected.id}/transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to_department: transferDept,
          to_role: transferRole || transferDept,
          to_level: transferLevel,
          reason: transferReason,
          finding: transferFinding || null,
          transferred_by: 'reviewer',
        }),
      });
      if (res.ok) {
        setShowTransfer(false);
        setTransferDept(''); setTransferRole(''); setTransferReason(''); setTransferFinding('');
        setSelected(null);
        load();
      } else {
        const err = await res.json();
        alert(`Error: ${err.detail || 'Transfer failed'}`);
      }
    } catch (e) { console.error(e); showToast('Transfer failed. Please try again.', false); }
    setActing(false);
  };

  const handleEscalate = async () => {
    if (!selected || !escalateReason.trim()) return;
    setActing(true);
    try {
      const res = await authFetch(`${API}/${selected.id}/escalate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reason: escalateReason,
          finding: escalateFinding || null,
          escalated_by: 'reviewer',
        }),
      });
      if (res.ok) {
        setShowEscalate(false);
        setEscalateReason(''); setEscalateFinding('');
        setSelected(null);
        load();
      } else {
        const err = await res.json();
        alert(`Error: ${err.detail || 'Escalation failed'}`);
      }
    } catch (e) { console.error(e); showToast('Escalation failed. Please try again.', false); }
    setActing(false);
  };

  const loadDetail = async (item: ActionItem) => {
    setSelected(item);
    setNotes('');
    try {
      const res = await authFetch(`${API}/${item.id}`);
      if (res.ok) setSelected(await res.json());
    } catch (e) { console.error(e); showToast('Failed to load details. Please try again.', false); }
  };

  const filteredItems = filter === 'all' ? items : items.filter(i => i.status === filter);
  const agent = getAgentForRole();

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading inbox...</div>;
  }

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8,
          background: toast.ok ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${toast.ok ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.4)'}`,
          color: toast.ok ? 'var(--positive)' : '#fca5a5', fontSize: 13, fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)', maxWidth: 420,
        }}>
          {toast.msg}
        </div>
      )}

      {/* Subtitle */}
      <div style={{ fontSize: 13, color: 'var(--text-hint)', marginBottom: 16 }}>
        Review queue for <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{role?.label || 'Unknown'}</span>
        {agent && <span> ({titleCase(agent)})</span>}
        {' '}&mdash; <span style={{ color: 'var(--warning)', fontWeight: 600 }}>{inboxTotal} item{inboxTotal !== 1 ? 's' : ''}</span> pending
      </div>

      {/* Split layout */}
      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1fr' : '1fr', gap: 16 }}>
        {/* Table side */}
        <div>
          <InboxTable
            filteredItems={filteredItems}
            selected={selected}
            inboxTotal={inboxTotal}
            inboxPage={inboxPage}
            pageSize={PAGE_SIZE}
            filter={filter}
            items={items}
            selectedDept={selectedDept}
            deptCounts={deptCounts}
            deptTotal={deptTotal}
            stats={stats}
            onSelectItem={loadDetail}
            onSetFilter={setFilter}
            onSetSelectedDept={setSelectedDept}
            onSetInboxPage={setInboxPage}
          />
        </div>

        {/* Detail panel */}
        {selected && (
          <ActionDetail
            selected={selected}
            notes={notes}
            acting={acting}
            flagReview={flagReview}
            onClose={() => setSelected(null)}
            onSetNotes={setNotes}
            onSetFlagReview={setFlagReview}
            onDoAction={doAction}
            onShowTransfer={() => setShowTransfer(true)}
            onShowEscalate={() => setShowEscalate(true)}
            onFetchControlGap={fetchControlGap}
            showToast={showToast}
            roleLevel={role.level}
          />
        )}
      </div>

      {/* Transfer Modal */}
      {showTransfer && selected && (
        <TransferModal
          selected={selected}
          transferDept={transferDept}
          transferRole={transferRole}
          transferLevel={transferLevel}
          transferReason={transferReason}
          transferFinding={transferFinding}
          acting={acting}
          onSetTransferDept={setTransferDept}
          onSetTransferRole={setTransferRole}
          onSetTransferLevel={setTransferLevel}
          onSetTransferReason={setTransferReason}
          onSetTransferFinding={setTransferFinding}
          onSubmit={handleTransfer}
          onClose={() => { setShowTransfer(false); setTransferReason(''); setTransferFinding(''); }}
        />
      )}

      {/* Control Gap Modal */}
      {showControlGap && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowControlGap(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-elevated)', borderRadius: 12, padding: 24, width: 520, maxHeight: '80vh', overflowY: 'auto', border: '1px solid var(--bg-border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Control Gap Analysis</h3>
              <button onClick={() => setShowControlGap(false)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 16 }}>X</button>
            </div>
            {controlGapLoading ? (
              <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-secondary)' }}>Loading...</div>
            ) : controlGap?.error ? (
              <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-secondary)' }}>No control gap data for this exception type.</div>
            ) : controlGap ? (
              <>
                {/* Root Cause */}
                <div style={{ marginBottom: 16, padding: 12, background: 'rgba(239,68,68,0.06)', borderRadius: 6, border: '1px solid #fef2f2' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', marginBottom: 6 }}>Root Cause</div>
                  <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 600, marginBottom: 4 }}>{controlGap.failure_pattern?.pattern_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{controlGap.failure_pattern?.root_cause}</div>
                  {controlGap.failure_pattern?.root_cause_detail?.length > 0 && (
                    <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                      {controlGap.failure_pattern.root_cause_detail.map((d: string, i: number) => (
                        <li key={i} style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 2 }}>{d}</li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Framework References */}
                {controlGap.framework_refs?.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--navy-800)', textTransform: 'uppercase', marginBottom: 8 }}>Framework References</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {controlGap.framework_refs.map((ref: any, i: number) => (
                        <span key={i} style={{ padding: '4px 10px', background: '#f5f3ff', border: '1px solid #f5f3ff', borderRadius: 6, fontSize: 11 }}>
                          <span style={{ color: 'var(--navy-800)', fontWeight: 700 }}>{ref.framework_code}-{ref.principle_code}</span>
                          <span style={{ color: 'var(--text-secondary)', marginLeft: 4 }}>{ref.principle_name}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Control Objective */}
                {controlGap.control_objective && (
                  <div style={{ marginBottom: 16, padding: 12, background: 'rgba(6,182,212,0.06)', borderRadius: 6, border: '1px solid var(--accent-border)' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#67e8f9', textTransform: 'uppercase', marginBottom: 6 }}>Control Should Be</div>
                    <div style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 600 }}>{controlGap.control_objective.objective_code}: {controlGap.control_objective.objective_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>{controlGap.control_objective.description}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 4 }}>Cycle: {controlGap.control_objective.cycle_name}</div>
                  </div>
                )}

                {/* Remediation */}
                {controlGap.remediation_plan?.length > 0 && (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--positive)', textTransform: 'uppercase', marginBottom: 8 }}>Recommended Fix</div>
                    {controlGap.remediation_plan.map((r: any, i: number) => (
                      <div key={i} style={{ padding: 10, background: 'rgba(34,197,94,0.04)', border: '1px solid rgba(34,197,94,0.12)', borderRadius: 6, marginBottom: 6 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 600 }}>{r.template_name}</span>
                          <span style={{ fontSize: 10, color: 'var(--positive)' }}>{r.steps?.length || 0} steps</span>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>{r.description}</div>
                        {r.expected_reduction_pct && (
                          <div style={{ fontSize: 10, color: 'var(--positive)', marginTop: 4 }}>Expected reduction: {r.expected_reduction_pct}%</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Risk info */}
                <div style={{ marginTop: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {controlGap.failure_pattern?.risk_category && (
                    <span style={{ padding: '3px 8px', background: 'rgba(251,191,36,0.1)', borderRadius: 4, fontSize: 10, color: 'var(--warning)', fontWeight: 600 }}>
                      {controlGap.failure_pattern.risk_category}
                    </span>
                  )}
                  {controlGap.failure_pattern?.typical_frequency && (
                    <span style={{ padding: '3px 8px', background: 'rgba(0,0,0,0.05)', borderRadius: 4, fontSize: 10, color: 'var(--text-secondary)' }}>
                      {controlGap.failure_pattern.typical_frequency}
                    </span>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* Escalate Modal */}
      {showEscalate && selected && (
        <EscalateModal
          selected={selected}
          escalateReason={escalateReason}
          escalateFinding={escalateFinding}
          acting={acting}
          onSetEscalateReason={setEscalateReason}
          onSetEscalateFinding={setEscalateFinding}
          onSubmit={handleEscalate}
          onClose={() => { setShowEscalate(false); setEscalateReason(''); setEscalateFinding(''); }}
        />
      )}
    </div>
  );
}
